import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseURL from '../assets/contants/BaseURL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, 
  faCheckCircle, 
  faArrowLeft, 
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${BaseURL}Userauth/password-reset-link/request_reset/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(data.error || 'Failed to send reset email. Please try again.');
      }
    } catch (err) {
      console.error('Error sending reset email:', err);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '400px',
      padding: '40px',
      textAlign: 'center',
      margin: '20px'
    },
    logo: {
      marginBottom: '30px',
      textAlign: 'center'
    },
    logoH2: {
      color: '#2c3e50',
      fontSize: '1.8rem',
      marginBottom: '5px'
    },
    logoP: {
      color: '#2c3e50',
      opacity: 0.7,
      fontSize: '0.9rem'
    },
    header: {
      marginBottom: '25px'
    },
    title: {
      color: '#2c3e50',
      fontSize: '1.5rem',
      marginBottom: '8px',
      fontWeight: '600'
    },
    subtitle: {
      color: '#7f8c8d',
      fontSize: '0.85rem',
      marginBottom: 0,
      lineHeight: '1.4'
    },
    form: {
      marginTop: '20px'
    },
    inputGroup: {
      marginBottom: '20px',
      textAlign: 'left'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: '#2c3e50',
      fontSize: '0.9rem'
    },
    inputField: {
      position: 'relative'
    },
    icon: {
      position: 'absolute',
      left: '15px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#2c3e50',
      opacity: 0.6,
      zIndex: 1
    },
    input: {
      width: '100%',
      padding: '12px 15px 12px 40px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '1rem',
      transition: 'all 0.3s',
      boxSizing: 'border-box',
      backgroundColor: 'white'
    },
    button: {
      width: '100%',
      padding: '12px',
      borderRadius: '4px',
      border: 'none',
      backgroundColor: '#3498db',
      color: 'white',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s',
      marginTop: '10px'
    },
    buttonDisabled: {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed',
      opacity: 0.7
    },
    footer: {
      marginTop: '30px',
      fontSize: '0.9rem',
      color: '#2c3e50',
      opacity: 0.7,
      textAlign: 'center'
    },
    errorMessage: {
      color: '#e74c3c',
      marginBottom: '15px',
      fontSize: '0.9rem',
      padding: '10px',
      backgroundColor: '#fde8e8',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    successBox: {
      textAlign: 'center'
    },
    successIcon: {
      marginBottom: '20px'
    },
    successTitle: {
      color: '#2c3e50',
      fontSize: '1.3rem',
      marginBottom: '10px',
      fontWeight: '600'
    },
    successMessage: {
      color: '#7f8c8d',
      marginBottom: '20px',
      fontSize: '0.85rem',
      lineHeight: '1.4'
    },
    emailHighlight: {
      color: '#3498db',
      fontWeight: '600'
    },
    backLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: '#3498db',
      textDecoration: 'none',
      fontSize: '0.85rem',
      marginTop: '15px',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0
    },
    tryAgainLink: {
      color: '#3498db',
      textDecoration: 'none',
      fontWeight: '600',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      fontSize: 'inherit'
    },
    spinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '3px solid rgba(255,255,255,.3)',
      borderRadius: '50%',
      borderTopColor: '#fff',
      animation: 'spin 1s ease-in-out infinite',
      marginRight: '8px',
      verticalAlign: 'middle'
    }
  };

  // Add global styles for animations
  React.useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      input:focus {
        outline: none;
        border-color: #3498db !important;
        box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2) !important;
      }
      
      button:hover:not(:disabled) {
        background-color: #2980b9 !important;
      }
      
      .back-link:hover {
        color: #2980b9 !important;
        text-decoration: underline !important;
      }
      
      .try-again:hover {
        text-decoration: underline !important;
      }
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  if (isSubmitted) {
    return (
      <div style={{
        backgroundColor: '#f5f7fa',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        margin: 0,
        padding: '20px'
      }}>
        <div style={styles.container}>
          <div style={styles.successBox}>
            <div style={styles.successIcon}>
              <FontAwesomeIcon icon={faCheckCircle} size="3x" color="#27ae60" />
            </div>
            <h2 style={styles.successTitle}>Check your email</h2>
            <p style={styles.successMessage}>
              We've sent a password reset link to{' '}
              <span style={styles.emailHighlight}>{email}</span>
            </p>

            <div style={{ marginTop: '20px' }}>
              <p style={{ fontSize: '0.8rem', color: '#7f8c8d', marginBottom: '15px' }}>
                Didn't receive the email? Check your spam folder or{' '}
                <a
                  onClick={() => {
                    setIsSubmitted(false);
                    setErrorMessage('');
                    setEmail('');
                  }}
                  style={styles.tryAgainLink}
                  className="try-again"
                >
                  try again
                </a>
              </p>

              <a
                onClick={() => navigate('/login')}
                style={styles.backLink}
                className="back-link"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to sign in
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#f5f7fa',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      margin: 0,
      padding: '20px'
    }}>
      <div style={styles.container}>
        <div style={styles.logo}>
          <h2 style={styles.logoH2}>CraneMonitor Pro</h2>
          <p style={styles.logoP}>Reset Your Password</p>
        </div>

        <div style={styles.header}>
          <h2 style={styles.title}>Forgot Password?</h2>
          <p style={styles.subtitle}>
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {errorMessage && (
          <div style={styles.errorMessage}>
            <FontAwesomeIcon icon={faExclamationCircle} />
            <span>{errorMessage}</span>
          </div>
        )}

        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="email">Email Address</label>
            <div style={styles.inputField}>
              <FontAwesomeIcon icon={faEnvelope} style={styles.icon} />
              <input
                id="email"
                name="email"
                type="email"
                style={styles.input}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrorMessage('');
                }}
                placeholder="Enter your email address"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <button
            type="submit"
            style={isLoading || !email ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
            disabled={isLoading || !email}
          >
            {isLoading ? (
              <>
                <span style={styles.spinner}></span>
                Sending...
              </>
            ) : (
              'Send reset link'
            )}
          </button>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <a
              onClick={() => navigate('/login')}
              style={styles.backLink}
              className="back-link"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to sign in
            </a>
          </div>
        </form>

        <div style={styles.footer}>
          <p>© 2026 CraneMonitor Pro. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;